import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import {MessageService} from 'primeng/api';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {FormFieldConfig} from '../../../../model/api/form-field-config';
import {AutoComplete} from "primeng/autocomplete";
import {CcSubFormBaseComponent} from "../cc-sub-form-base.component";
import {FindOptionTextPipe} from "../../../../pipe/clearing/find-option-text-pipe.component";
import {Password} from "primeng/password";
import {FileUpload} from "primeng/fileupload";
import {AppModule} from "../../../../app.module";

@Component({
  selector: 'app-cc-field-dispatcher[fieldConfig][formRecord][transPartKey][loading]',
  templateUrl: './cc-field-dispatcher.component.html',
  styleUrls: ['./cc-field-dispatcher.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    TranslateService,
    FindOptionTextPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CcFieldDispatcherComponent<T = any> extends CcSubFormBaseComponent {

  FieldTypes = FormFieldConfig.TYPE;

  fieldEventMap = {};

  fieldValueMap = {};

  MIN_CHAR_COUNT_AUTOCOMPLETE = 2;

  // Explicit toolbar for every RICHTEXTAREA field (PrimeNG p-editor -> Quill), rather than
  // PrimeNG's default toolbar: Quill 2.0.3 has an unpatched XSS (CVE-2025-15056) in the formula
  // and video blots' HTML export - not reachable today since this app reads the editor's live
  // .innerHTML rather than calling Quill's getSemanticHTML()/getHTML(), but omitting those two
  // buttons here means there's no UI path to insert either blot type at all, closing the gap
  // outright rather than relying on how the content happens to be extracted.
  richTextEditorModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{list: 'ordered'}, {list: 'bullet'}],
      ['link'],
      ['clean']
    ]
  };

  @Input()
  fieldConfig: FormFieldConfig;

  @Input()
  formRecord: any;
  @Output()
  formRecordChange = new EventEmitter<any>();

  @Input()
  transPartKey: String;

  @Input()
  loading: boolean = false;

  @Input()
  loadingProgressValue: number = 100;

  @ViewChildren('ccSubForm')
  subFormComponents: QueryList<CcSubFormBaseComponent>;

  @ViewChild('passwordField')
  passwordField: Password;

  @ViewChild('inputField')
  inputField: ElementRef<HTMLInputElement>;

  previousAutoCompleteValue: string = "";

  focusedInit: boolean = false;

  constructor(cdref: ChangeDetectorRef,
              messageService: MessageService,
              translateService: TranslateService) {
    super(cdref, messageService, translateService);
  }

  // tslint:disable-next-line:use-life-cycle-interface
  ngOnInit(): void {
    this.initConfigs();
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.initConfigs();
      this.reloadFieldValueMap();
    })
    this.reloadFieldValueMap();
    if (this.passwordField) {
      this.passwordField.input.nativeElement.autocomplete = 'new-password';
      this.passwordField.input.nativeElement.autocorrect = 'off';
    }
  }

  // private doFocus() {
  //   if (this.fieldConfig.focused) {
  //     if (!this.focusedInit && this.inputField
  //       && !(this.loading || this.loadingProgressValue < 100
  //         || this.fieldValueMap['disabled'])) {
  //       this.focusedInit = true;
  //       setTimeout(() => this.inputField?.nativeElement?.focus(), 500);
  //     }
  //   }
  // }

  protected getFields(fieldResult) {
    if (fieldResult.success) {
      return fieldResult.records;
    } else {
      this.errorMessage(fieldResult.message);
      return undefined;
    }
  }

  ngOnDestroy(): void {
  }


  multiTextAdd(value, field: string, changed?: (event: any, event2: any) => void) {
    if (!this.formRecord[field]) {
      this.formRecord[field] = [];
    }
    this.formRecord[field].push(value);
    if (changed) {
      changed(this.formRecord[field], undefined);
    }
  }

  multiTextRemove(value, field: string, changed?: (event: any, event2: any) => void) {
    if (!this.formRecord[field]) {
      this.formRecord[field] = [];
    }
    let found = 0;
    this.formRecord[field] = this.formRecord[field].filter(m => {
      if (value === m) {
        found++;
      }
      return value !== m || found > 1;
    });
    if (changed) {
      changed(this.formRecord[field], undefined);
    }
  }

  addEvent(field: string, event: EventEmitter<any>) {
    this.fieldEventMap[field] = event;
  }

  autoCompleteSearch(fieldConfig: FormFieldConfig, autocompField: AutoComplete) {
    const query = autocompField.inputValue;
    const filteredOptions = fieldConfig.options(this.formRecord, query).filter(option => !query || (query.includes('*') ?
      option.text.toUpperCase().startsWith(query.replace('*', '').toUpperCase())
      : option.text.toUpperCase().startsWith(query.toUpperCase())));
    autocompField.suggestions = filteredOptions
    this.reloadFieldValueMap();
    this.formRecordChange.emit(this.formRecord);
  }

  autoCompleteSearchKeyUp(
    fieldConfig: FormFieldConfig,
    autocompField: AutoComplete,
    $event: KeyboardEvent
  ) {
    const input = autocompField.inputEL.nativeElement as HTMLInputElement;
    const query = autocompField.inputValue;

    // 🔹 SAVE CURSOR POSITION
    const cursorStart = input.selectionStart ?? query.length;
    const cursorEnd = input.selectionEnd ?? query.length;

    const isDeleteKey: Boolean = $event.code === 'Backspace' || $event.code === 'Delete';
    if (!isDeleteKey && ($event.key.length > 1 || ($event.ctrlKey && $event.key !== 'v'))) {
      return;
    }

    this.formRecord[fieldConfig.field] = '';
    this.reloadFieldValueMap();
    this.formRecordChange.emit(this.formRecord);

    const filteredOptions = fieldConfig
      .options(this.formRecord, query)
      .filter(option =>
        !query ||
        (query.includes('*')
          ? option.text.toUpperCase().startsWith(query.replace('*', '').toUpperCase())
          : option.text.toUpperCase().startsWith(query.toUpperCase()))
      );

    autocompField.suggestions = filteredOptions;

    let didAutocomplete = false;

    if (
      !isDeleteKey &&
      query &&
      query.length > this.MIN_CHAR_COUNT_AUTOCOMPLETE &&
      filteredOptions.length > 0 &&
      query.length >= this.previousAutoCompleteValue.length
    ) {
      this.formRecord[fieldConfig.field] = filteredOptions[0].text;
      filteredOptions[0]['select'] = false;
      fieldConfig.changed ? fieldConfig.changed(filteredOptions[0], this.formRecord) : {};

      didAutocomplete = true;
    } else {
      this.formRecord[fieldConfig.field] = query;
      fieldConfig.changed
        ? fieldConfig.changed(
          {
            text: query,
            id: query,
            select: false
          },
          this.formRecord
        )
        : {};
    }

    this.reloadFieldValueMap();
    this.formRecordChange.emit(this.formRecord);

    // 🔹 RESTORE SELECTION LOGIC
    queueMicrotask(() => {
      if (didAutocomplete) {
        // ORIGINAL BEHAVIOUR: select autocompleted part
        this.createSelection(
          input,
          query.length,
          this.formRecord[fieldConfig.field].length
        );
      } else {
        // FIX: restore previous cursor instead of jumping to end
        input.setSelectionRange(cursorStart, cursorEnd);
      }
    });

    this.previousAutoCompleteValue = query;
  }


  createSelection(field, start, end) {
    if (field.createTextRange) {
      var selRange = field.createTextRange();
      selRange.collapse(true);
      selRange.moveStart('character', start);
      selRange.moveEnd('character', end - start);
      selRange.select();
    } else if (field.setSelectionRange) {
      field.setSelectionRange(start, end);
    } else if (field.selectionStart) {
      field.selectionStart = start;
      field.selectionEnd = end;
    }
    field.focus();
  }

  autoCompleteCleared(fieldConfig: any, $event: Event) {
    this.formRecord[fieldConfig.field] = undefined;
    this.reloadFieldValueMap();
    this.formRecordChange.emit(this.formRecord);
    this.previousAutoCompleteValue = '';
  }

  dropDownClick(fieldConfig: FormFieldConfig, autocompField: AutoComplete, $event: any) {
    const query = this.formRecord[fieldConfig.field];
    if (!query || query.length == 0) {
      autocompField.clear();
    }
  }

  autoCompleteSelect(fieldConfig: FormFieldConfig, autocompField: AutoComplete, $event: any) {
    this.formRecord[fieldConfig.field] = $event.id;
    autocompField.inputValue = $event.text;
    $event['select'] = true;
    fieldConfig.changed ? fieldConfig.changed($event, this.formRecord) : {};
    this.reloadFieldValueMap();
    this.formRecordChange.emit(this.formRecord);
  }

  recordChanged(fieldConfig: FormFieldConfig, record: any) {
    if (fieldConfig.type === this.FieldTypes.MULTISELECT
      || fieldConfig.type === this.FieldTypes.DROPDOWN
      || fieldConfig.type === this.FieldTypes.LISTBOX
      || fieldConfig.type === this.FieldTypes.PICKLIST
      || fieldConfig.type === this.FieldTypes.INTEGER
      || fieldConfig.type === this.FieldTypes.DECIMAL
    ) {
      this.formRecord[fieldConfig.field] = record.value;
    } else if (fieldConfig.type === this.FieldTypes.SINGLERICHFIELD
      || fieldConfig.type === this.FieldTypes.RICHFIELD
      || fieldConfig.type === this.FieldTypes.ADDDELETEFIELD
      || fieldConfig.type === this.FieldTypes.TABLEFORMFIELD
      || fieldConfig.type === this.FieldTypes.MAPFIELD)
      this.formRecord[fieldConfig.field] = record;
    if (fieldConfig.changed)
      fieldConfig.changed(record, this.formRecord);
    this.reloadFieldValueMap();
    this.formRecordChange.emit(this.formRecord);
  }

  onPickListChange(fieldConfig: FormFieldConfig): void {
    if (fieldConfig.changed)
      fieldConfig.changed(this.formRecord[fieldConfig.field], this.formRecord);
    this.reloadFieldValueMap();
  }

  // PrimeNG's pickList always appends moved items to the end of the target
  // list. Re-insert the just-moved items at the top instead.
  onPickListMoveToTarget( fieldConfig: FormFieldConfig, event: { items: any[] }): void {
    const target: any[] = this.formRecord[fieldConfig.field];
    if (target && event?.items?.length) {
      const movedItems = event.items.filter(item => target.includes(item));
      movedItems.forEach(item => target.splice(target.indexOf(item), 1));
      target.unshift(...movedItems);
    }
    this.onPickListChange(fieldConfig);
  }

  fileSelected(fieldConfig: any, event: any, fileUpload: FileUpload) {
    if (event.files && event.files.length > 0) {
      const file = event.files[0];
      this.formRecord[fieldConfig.field] = file;
    }
    fileUpload.clear();
    this.reloadFieldValueMap();
    this.formRecordChange.emit(this.formRecord);
  }

  public reloadFieldValueMap(): void {
    // if(this.fieldConfig.field=="name"){
    //   debugger;
    // }
    if (this.fieldConfig) {
      if (this.fieldConfig.validate && this.fieldConfig.type != this.FieldTypes.NONE)
        this.fieldValueMap['validate'] = this.fieldConfig.validate(this.formRecord);
      if (this.fieldConfig.hidden)
        this.fieldValueMap['hiddenField'] = this.fieldConfig.hidden(this.formRecord);
      if (this.fieldConfig.options) {
        this.fieldValueMap['options'] = this.fieldConfig.options(this.formRecord);
      }
      if (this.fieldConfig.icon) {
        if (this.fieldConfig.options) {
          const options: any[] = this.fieldConfig.options(this.formRecord);
          if (options)
            options.forEach(option => this.fieldValueMap['iconItem' + '_' + option.id] = this.fieldConfig.icon(option.id, this.formRecord));
        }
        this.fieldValueMap['icon'] = this.fieldConfig.icon(this.formRecord ? this.formRecord[this.fieldConfig.field] : undefined, this.formRecord);
      }
      if (this.fieldConfig.iconValue) {
        this.fieldValueMap['iconValue'] = this.fieldConfig.iconValue(this.formRecord ? this.formRecord[this.fieldConfig.field] : undefined, this.formRecord);
        if (this.fieldConfig.options) {
          const options: any[] = this.fieldConfig.options(this.formRecord);
          if (options)
            options.forEach(option => this.fieldValueMap['iconValueItem' + '_' + option.id] = this.fieldConfig.iconValue(option.id, this.formRecord));
        }
      }
      if (this.fieldConfig.buttons) {
        this.fieldConfig.buttons.forEach((btn, index) => {
          if (btn.disabledFct)
            this.fieldValueMap['disabled_btn' + index] = btn.disabledFct(this.formRecord);
        });
      }
      if (this.fieldConfig.disabled) {
        this.fieldValueMap['disabled'] = this.fieldConfig.disabled(this.formRecord);
      }
      if (this.fieldConfig.findOptionText) {
        this.fieldValueMap['findOptionText'] = this.fieldConfig.findOptionText(this.formRecord[this.fieldConfig.field]);
      }
      if (this.fieldConfig.style) {
        this.fieldValueMap['style'] = this.fieldConfig.style(this.formRecord);
      }
      if (this.fieldConfig.fractionDigits) {
        this.fieldValueMap['fractionDigits'] = this.fieldConfig.fractionDigits(this.formRecord);
      }
      if (this.fieldConfig.hybridType) {
        this.fieldValueMap['hybridType'] = this.fieldConfig.hybridType(this.formRecord);
      }
      if (this.subFormComponents) {
        this.subFormComponents.forEach(ccForm => ccForm.reloadFieldValueMap());
      }
      // this.doFocus();
      this.markForCheck();
    }
  }
}
